/**
 * La busqueda de productos, compartida.
 *
 * POR QUE ESTA ACA Y NO ADENTRO DE CADA PANTALLA
 * ----------------------------------------------
 * Hay DOS lugares desde donde se busca un producto, y hacen trabajos distintos:
 *
 *   · El overlay ⌘K, para CONSULTAR desde cualquier pantalla mientras se hace
 *     otra cosa. No navega, no toca nada, y al cerrarse deja todo como estaba.
 *   · El campo adentro del ticket, para ARMAR una venta. Se cargan cuatro
 *     productos seguidos sin que se abra y cierre un modal cuatro veces.
 *
 * Son dos superficies porque son dos tareas. Pero la LOGICA tiene que ser una
 * sola: si el debounce, el minimo de letras o el orden de resultados difieren,
 * el mismo texto encuentra cosas distintas segun donde lo escribas, y eso es
 * imposible de explicar y peor de depurar.
 */
import { useEffect, useRef, useState } from "react";

import { api, mensajeDeError } from "@/lib/api";

export interface ProductoEncontrado {
  producto_id: number;
  codigo_interno: string;
  nombre: string;
  /**
   * La ficha del equipo. El color, la memoria y la bateria salieron de adentro
   * del nombre (0055, 0056, 0073): sin ellos, dos unidades del mismo modelo se
   * leen igual justo donde hay que elegir cual se le ofrece al cliente. La
   * pantalla arma el nombre con `catalogo/nombre.ts`.
   */
  color: string | null;
  es_usado: boolean;
  memoria_gb: number | null;
  bateria_pct: number | null;
  rubro: string | null;
  precio: string;
  precio_moneda: "ARS" | "USD";
  precio_ars: string;
  precio_usd: string;
  stock_actual: string;
  disponibilidad: "DISPONIBLE" | "A_PEDIDO" | "SIN_STOCK";
  /** El flag del producto: con stock puede dejar igual un encargo (0115). */
  a_pedido?: boolean;
  margen_ars: string;
  /**
   * El costo en pesos. Sin el, un producto sin costo cargado mostraba como
   * «margen» el precio entero (precio - 0).
   */
  costo_ars?: string | null;
  texto_whatsapp: string;
  /** El IMEI del usado (0130): distingue dos unidades iguales en el mostrador. */
  imei?: string | null;
}

/** Dos letras: con una sola, cualquier catalogo devuelve ruido. */
export const MINIMO_LETRAS = 2;
/**
 * 220 ms. Lo suficiente para no disparar una consulta por tecla, lo bastante
 * poco para que con alguien esperando no se sienta lento.
 */
export const DEBOUNCE_MS = 220;

export interface Busqueda {
  resultados: ProductoEncontrado[];
  cargando: boolean;
  error: string | null;
  /** true cuando se busco de verdad y no hubo nada. */
  sinResultados: boolean;
}

export function useBusquedaProductos(texto: string, limite = 8): Busqueda {
  const [resultados, setResultados] = useState<ProductoEncontrado[]>([]);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const q = texto.trim();

  useEffect(() => {
    if (q.length < MINIMO_LETRAS) {
      setResultados([]);
      setCargando(false);
      setError(null);
      // Cancelar lo que este en vuelo no es opcional: sin esto, borrar el texto
      // deja llegando una respuesta vieja que repuebla la lista ya vaciada.
      abortRef.current?.abort();
      return;
    }

    setCargando(true);
    const t = setTimeout(async () => {
      abortRef.current?.abort();
      const ctrl = new AbortController();
      abortRef.current = ctrl;

      try {
        const r = await api.get<{ resultados: ProductoEncontrado[] }>(
          `/precios?q=${encodeURIComponent(q)}&limite=${limite}`,
          { signal: ctrl.signal },
        );
        if (ctrl.signal.aborted) return;
        setResultados(r?.resultados ?? []);
        setError(null);
      } catch (e) {
        if (ctrl.signal.aborted) return;
        setError(mensajeDeError(e));
        setResultados([]);
      } finally {
        if (!ctrl.signal.aborted) setCargando(false);
      }
    }, DEBOUNCE_MS);

    return () => clearTimeout(t);
  }, [q, limite]);

  useEffect(() => () => abortRef.current?.abort(), []);

  return {
    resultados,
    cargando,
    error,
    sinResultados: q.length >= MINIMO_LETRAS && !cargando && !error && resultados.length === 0,
  };
}

/** Como se comunica la disponibilidad. Nunca el numero exacto de stock al cliente. */
export const DISPONIBILIDAD: Record<
  ProductoEncontrado["disponibilidad"],
  { texto: string; clase: string }
> = {
  DISPONIBLE: { texto: "En stock", clase: "text-success" },
  A_PEDIDO: { texto: "A pedido", clase: "text-warning" },
  SIN_STOCK: { texto: "Sin stock", clase: "text-destructive" },
};
